import { TIME_CONVERSION } from '@shared/constants';
import type { AppError, AsyncResult } from '@shared/utils';
import { err, ok, tryCatchAsync } from '@shared/utils';
import { SignJWT, importPKCS8 } from 'jose';
import { JITSI_CONSTANTS } from '../constants/JITSI_CONSTANTS';

interface MintJaasJwtInput {
  apiKeyId: string;
  appId: string;
  isModerator: boolean;
  privateKey: string;
  roomName: string;
  ttlSeconds: number;
  userId: string;
  userName: string;
}

/**
 * @group Functions
 *
 * @summary
 * <blockquote>Mints a RS256 JWT with JaaS claims (aud=jitsi, iss=chat, sub=appId)
 * for Jitsi Meet authentication.</blockquote>
 *
 * @example
 * ```typescript
 * import { mintJaasJwt } from '@shared/telehealth/jitsi';
 *
 * const result = await mintJaasJwt({
 *   appId: 'vpaas-magic-cookie-xxx',
 *   apiKeyId: 'key-id',
 *   privateKey: '-----BEGIN PRIVATE KEY-----...',
 *   roomName: 'room-123',
 *   userId: 'user-1',
 *   userName: 'Dr. Smith',
 *   isModerator: true,
 *   ttlSeconds: 600,
 * });
 * ```
 */
export async function mintJaasJwt(
  input: MintJaasJwtInput
): AsyncResult<string, AppError> {
  const keyResult = await tryCatchAsync(() =>
    importPKCS8(input.privateKey, JITSI_CONSTANTS.JWT_ALGORITHM)
  );
  if (keyResult.isErr) {
    return err({
      errorCode: 'JWT_KEY_IMPORT_FAILED',
      errorMessage: keyResult.error instanceof Error ? keyResult.error.message : String(keyResult.error),
    });
  }

  const nowSeconds = Math.floor(Date.now() / TIME_CONVERSION.MILLISECONDS_PER_SECOND);

  const jwtResult = await tryCatchAsync(async () => {
    const jwt = await new SignJWT({
      aud: JITSI_CONSTANTS.JWT_AUDIENCE,
      context: {
        user: {
          id: input.userId,
          moderator: input.isModerator ? 'true' : 'false',
          name: input.userName,
          email: '',
        },
        features: {
          livestreaming: 'false',
          'outbound-call': 'false',
          'sip-outbound-call': 'false',
          transcription: 'false',
          recording: 'false',
        },
        room: {
          regex: false,
        },
      },
      iss: JITSI_CONSTANTS.JWT_ISSUER,
      nbf: nowSeconds,
      room: input.roomName,
      sub: input.appId,
    })
      .setProtectedHeader({ alg: JITSI_CONSTANTS.JWT_ALGORITHM, kid: input.apiKeyId, typ: JITSI_CONSTANTS.JWT_TYPE })
      .setExpirationTime(nowSeconds + input.ttlSeconds)
      .setIssuedAt(nowSeconds)
      .sign(keyResult.value);

    return jwt;
  });

  if (jwtResult.isErr) {
    return err({
      errorCode: 'JWT_SIGN_FAILED',
      errorMessage: jwtResult.error instanceof Error ? jwtResult.error.message : String(jwtResult.error),
    });
  }

  return ok(jwtResult.value);
}
